import { trackCtaClick } from "@/components/analytics/conversions";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, ExternalLink } from "lucide-react";

export interface CalculatorArticleSection {
  id: string;
  title: string;
  paragraphs: string[];
  subsections?: {
    title: string;
    paragraphs: string[];
    bullets?: string[];
  }[];
  bullets?: string[];
  steps?: string[];
  table?: {
    caption: string;
    headers: string[];
    rows: string[][];
  };
  note?: string;
  sourceIds?: string[];
}

export interface CalculatorArticleLink {
  href: string;
  label: string;
  description: string;
}

export interface CalculatorArticleContent {
  id: string;
  eyebrow: string;
  updatedAt: string;
  articleTitle: string;
  intro: string;
  sections: CalculatorArticleSection[];
  faqs: { question: string; answer: string }[];
  sources: { id: string; label: string; href: string }[];
  related: CalculatorArticleLink[];
  primaryCta: CalculatorArticleLink;
}

function TrackedLink({
  link,
  ctaName,
  primary = false,
}: {
  link: CalculatorArticleLink;
  ctaName: string;
  primary?: boolean;
}) {
  const content = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block break-words font-semibold text-foreground">
          {link.label}
        </span>
        <span className="mt-1 block break-words text-sm opacity-80">
          {link.description}
        </span>
      </span>
      <ArrowRight className="size-5 shrink-0" aria-hidden />
    </>
  );

  if (primary) {
    return (
      <Button
        asChild
        size="lg"
        className="h-auto min-h-14 w-full min-w-0 justify-between whitespace-normal py-3 text-left"
      >
        <a href={link.href} onClick={() => trackCtaClick(ctaName, link.href)}>
          {content}
        </a>
      </Button>
    );
  }

  return (
    <a
      href={link.href}
      onClick={() => trackCtaClick(ctaName, link.href)}
      className="flex min-h-14 min-w-0 items-center justify-between gap-4 rounded-xl border border-border bg-card p-5 transition-colors hover:border-primary/50 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {content}
    </a>
  );
}

function Contents({
  content,
  mobile = false,
}: { content: CalculatorArticleContent; mobile?: boolean }) {
  const links = [
    ...content.sections.map((section) => ({
      id: section.id,
      title: section.title,
    })),
    { id: `${content.id}-sss`, title: "Sık sorulan sorular" },
    { id: `${content.id}-kaynakca`, title: "Kaynakça" },
  ];

  if (mobile) {
    return (
      <details className="not-prose my-8 rounded-xl border border-border bg-card p-4 xl:hidden">
        <summary className="min-h-11 cursor-pointer font-semibold leading-11">
          İçindekiler
        </summary>
        <ol className="mt-3 space-y-1 border-border border-t pt-3 text-sm">
          {links.map((link, index) => (
            <li key={link.id}>
              <a
                className="flex min-h-11 items-center gap-3 rounded-md px-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                href={`#${link.id}`}
              >
                <span className="text-primary tabular-nums">
                  {String(index + 1).padStart(2, "0")}
                </span>
                {link.title}
              </a>
            </li>
          ))}
        </ol>
      </details>
    );
  }

  return (
    <nav aria-label="Rehber içindekiler" className="sticky top-28">
      <h2 className="mb-5 font-bold text-xl">İçindekiler</h2>
      <ol className="space-y-1 text-muted-foreground text-sm">
        {links.map((link, index) => (
          <li key={link.id}>
            <a
              className="flex min-h-10 items-center gap-3 rounded-md px-2 hover:bg-muted hover:text-foreground"
              href={`#${link.id}`}
            >
              <span className="text-primary tabular-nums">
                {String(index + 1).padStart(2, "0")}
              </span>
              {link.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function CalculatorArticle({
  content,
}: { content: CalculatorArticleContent }) {
  const faqId = `${content.id}-sss`;
  const sourcesId = `${content.id}-kaynakca`;
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: content.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  return (
    <section className="-translate-x-1/2 relative left-1/2 mt-16 w-[min(calc(100vw-2rem),72rem)] border-border/70 border-t pt-4 sm:w-[min(calc(100vw-3rem),72rem)]">
      <script type="application/ld+json">
        {JSON.stringify(faqStructuredData)}
      </script>

      <div className="grid items-start py-10 xl:grid-cols-[minmax(12rem,1fr)_minmax(0,40rem)_minmax(12rem,1fr)] xl:gap-8 xl:py-14">
        <aside className="hidden xl:block">
          <Contents content={content} />
        </aside>

        <article
          lang="tr"
          className="prose prose-zinc dark:prose-invert mx-auto prose-h2:mt-14 prose-h3:mt-9 w-full min-w-0 max-w-[40rem] prose-headings:scroll-mt-28 prose-a:text-primary prose-h2:text-2xl prose-h3:text-lg prose-p:leading-8 xl:col-start-2"
        >
          <p className="not-prose text-muted-foreground text-sm">
            AtalayTheCoach · {content.eyebrow} · Güncellendi:{" "}
            {content.updatedAt}
          </p>
          <h2>{content.articleTitle}</h2>
          <p className="lead">{content.intro}</p>
          <Contents content={content} mobile />

          {content.sections.map((section) => (
            <section key={section.id}>
              <h2 id={section.id}>{section.title}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.bullets && (
                <ul>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}
              {section.steps && (
                <ol>
                  {section.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              )}
              {section.subsections?.map((subsection) => (
                <div key={subsection.title}>
                  <h3>{subsection.title}</h3>
                  {subsection.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  {subsection.bullets && (
                    <ul>
                      {subsection.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              {section.table && (
                <div
                  className="not-prose my-7 max-w-full overflow-x-auto rounded-xl border border-border"
                  tabIndex={0}
                >
                  <table className="w-full min-w-[34rem] border-collapse text-left text-sm">
                    <caption className="border-border border-b bg-muted/40 px-4 py-3 text-left font-semibold">
                      {section.table.caption}
                    </caption>
                    <thead>
                      <tr>
                        {section.table.headers.map((header) => (
                          <th
                            key={header}
                            className="border-border border-b px-4 py-3 font-semibold"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.rows.map((row) => (
                        <tr key={row.join("-")}>
                          {row.map((cell, index) => (
                            <td
                              key={`${cell}-${index}`}
                              className="border-border border-b px-4 py-3 align-top last:border-b-0"
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {section.note && (
                <aside className="not-prose my-7 rounded-xl border border-primary/20 bg-primary/5 p-5 leading-7">
                  <strong className="block">Kısa not</strong>
                  <p className="mt-2 text-muted-foreground">{section.note}</p>
                </aside>
              )}
              {section.sourceIds && (
                <p className="not-prose mt-5 text-muted-foreground text-sm">
                  Kaynaklar:{" "}
                  {section.sourceIds.map((sourceId, index) => (
                    <span key={sourceId}>
                      {index > 0 && ", "}
                      <a
                        className="font-medium text-primary underline underline-offset-4"
                        href={`#${content.id}-source-${sourceId}`}
                      >
                        [{sourceId}]
                      </a>
                    </span>
                  ))}
                </p>
              )}
            </section>
          ))}

          <section id={faqId} className="scroll-mt-28">
            <h2>Sık Sorulan Sorular</h2>
            <Accordion type="single" collapsible className="not-prose w-full">
              {content.faqs.map((faq, index) => (
                <AccordionItem value={`faq-${index + 1}`} key={faq.question}>
                  <AccordionTrigger className="min-h-14 py-4 text-left text-base hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-base text-muted-foreground leading-7">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section id={sourcesId} className="scroll-mt-28">
            <h2>Kaynakça</h2>
            <ol>
              {content.sources.map((source) => (
                <li key={source.id} id={`${content.id}-source-${source.id}`}>
                  <a
                    href={source.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    [{source.id}] {source.label}
                    <ExternalLink className="ml-1 inline size-3" aria-hidden />
                  </a>
                </li>
              ))}
            </ol>
          </section>
        </article>
      </div>

      <section
        aria-labelledby={`${content.id}-next-title`}
        className="mx-auto max-w-4xl py-10 sm:py-12"
      >
        <div className="mb-6 flex items-center gap-3">
          <BookOpen className="size-6 text-primary" aria-hidden />
          <h2 id={`${content.id}-next-title`} className="font-bold text-2xl">
            Sonraki adım
          </h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {content.related.map((link) => (
            <TrackedLink
              key={link.href}
              link={link}
              ctaName={`${content.id}_related_calculator`}
            />
          ))}
        </div>
        <div className="mt-6 rounded-2xl border border-primary/20 bg-primary/5 p-4 sm:p-6">
          <TrackedLink
            link={content.primaryCta}
            ctaName={`${content.id}_primary_cta`}
            primary
          />
        </div>
      </section>
    </section>
  );
}
